import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { TranslationService } from "../../services/TranslationService";
import { Redirect, useLocation, useHistory } from "react-router-dom";
import TabPosition from "../shared/TabPosition";
import ClientService, {
  ClientFields,
  getClientDefaultFieldProperty,
  getClientFieldDisplayValue,
  getClientFieldIsDefault
} from "../../services/ClientService";
import { ToastService } from "../shared/bootstrap/Toast";
import CompanyService, {
  DataType,
  Entities
} from "../../services/CompanyService";
import { Client } from "./entities/ClientGetResponse";
import {
  formatCurrency,
  formatDateDigits,
  formatInteger,
  formatIntizaOldDate,
  formatNumber
} from "../../utils/FormatUtils";
import whatsappIco from "../../assets/img/ico-whatsapp.png";
import Loading from "../shared/components/Loading";
import InvoiceList from "./invoice/InvoiceList";
import { EntityField, sortEntityFields } from "../shared/entities/EntityField";
import ClientDetailContext, {
  ClientDetailProvider
} from "./ClientDetailContext";
import { ClientDetailLeftMenuButtons } from "./components/ClientDetailLeftMenuButtons";
import {
  AutocompleteClient,
  AutocompleteGroup,
  DatePickerEditor,
  NumberInput
} from "../shared/components/Editors";
import TableContext from "../task/TableContext";
import Table, { TableHeader } from "../shared/Table";
import { Helmet } from "react-helmet-async";
import { GroupListResponse } from "../group/entities/GroupListResponse";
import ModalService from "../shared/bootstrap/modal/Modal";
import TooltipComponent from "../shared/TooltipComponent";
import ClientEdit from "./ClientEdit";
import FloatingPanelService from "../shared/FloatingPanel";
import BreadcrumbHistory from "../shared/BreadcrumbHistory";
import { ClientIterator } from "./ClientIterator";
import Dropdown from "../shared/components/Dropdown";
import { InvoiceGroups } from "./invoice/InvoiceGroups";
import { StorageService } from "../../services/StorageService";
import ActivityHistory from "./activity/ActivityHistory";
import CurrencyService from "../../services/CurrencyService";
import RequestService from "../../services/RequestService";
import { BadlySpelled2, Cast } from "../../utils/Utils";
import InvoiceGroupProvider from "./invoice/providers/InvoiceTableProvider";
import {
  handleErrorWithToast,
  useRequestAborter
} from "../../utils/RequestUtils";
import { ClientDashboardExtraValues } from "./components/ClientDashboardExtraValues";
import { FixToTop } from "../shared/FixToTop";
import SearchInput from "../shared/components/SearchInput";
import DashboardClientPanel from "../company/DashboardPanel/DashboardClientPanel";
import DashboardClientSales from "./components/DashboardClientSales";
import ReactSwitch from "react-switch";
import { OptionTab, ScrollNavChips } from "../shared/ScrollNavChips";

import iconHouse from "../../assets/img/icon-house.svg";
import iconInvoice from "../../assets/img/icon-invoice.svg";
import iconTasks from "../../assets/img/icon-check-task.svg";
import iconHistory from "../../assets/img/icon-history.svg";
import iconInactiveHouse from "../../assets/img/iconInactive-house.svg";
import iconInactiveInvoice from "../../assets/img/iconInactive-invoice.svg";
import iconInactiveHistory from "../../assets/img/iconInactive-history.svg";
import iconInactiveTasks from "../../assets/img/icon-inactive-check-task.svg";
import iconAgingActive from "../../assets/img/icon-aging-active.svg";
import iconAgingInactive from "../../assets/img/icon-aging-inactive.svg";
import InvoiceGroupTableProvider, {
  InvoiceGroupTableContextValues
} from "./providers/InvoiceGroupTableProvider";
import AuthenticationService from "../../services/AuthenticationService";
import AuthenticationContext from "../shared/AuthenticationContext";

export enum ModalTypeDefault {
  Email = "1",
  Comments = "2",
  Tasks = "3",
  SMS = "4",
  WhastApp = "6"
}

export const getClientFieldDisplayValueHtml = (
  field: EntityField,
  client: Client,
  highlight: boolean,
  copyText?: (text: string) => void,
  currencyId: number = CompanyService.getDefaultCurrencyId(),
  tooltipOrigin: boolean = false
) => {
  let displayValue = getClientFieldDisplayValue(field, client);

  if (field.id === ClientFields.followupMail) {
    return (
      <>
        {client.followUpMail}
        {copyText && (
          <TooltipComponent
            title={TranslationService.translate.CopyToClipboard}
            placement="right"
          >
            <i
              className="fal fa-copy is-link px-2"
              onClick={() => {
                copyText(displayValue);
              }}
            ></i>
          </TooltipComponent>
        )}
      </>
    );
  }

  if (!displayValue) {
    return undefined;
  }

  switch (field.type) {
    case DataType.Date:
      return formatDateDigits(new Date(displayValue));
    case DataType.Link:
      return highlight ? (
        <>
          <a
            target="_blank"
            rel="noopener noreferrer"
            className="overflow-hidden"
            href={displayValue}
          >
            {displayValue}
          </a>
          <a target="_blank" rel="noopener noreferrer" href={displayValue}>
            <i className="far fa-external-link ms-2"></i>
          </a>
        </>
      ) : (
        <a target="_blank" rel="noopener noreferrer" href={displayValue}>
          {displayValue}
          <i className="far fa-external-link ms-2"></i>
        </a>
      );
    case DataType.Number:
      displayValue = formatNumber(displayValue);
      break;
    case DataType.Currency:
      return <>{formatCurrency(displayValue, currencyId)}</>;
    case DataType.Phone:
      return highlight ? (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "8px"
          }}
        >
          <a href={"tel:" + CompanyService.getPhoneLink(displayValue)}>
            {displayValue}
          </a>
          {field.Whatsapp && (
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={CompanyService.getWhatsappUrl(displayValue)}
            >
              <img
                src={whatsappIco}
                width="20"
                height="20"
                alt="whatsapp icon"
              />
            </a>
          )}
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "8px"
          }}
        >
          <a
            href={"tel:" + CompanyService.getPhoneLink(displayValue)}
            className={`col-auto ${tooltipOrigin ? " ms-3" : ""}`}
          >
            {displayValue}
          </a>
          {field.Whatsapp && (
            <a
              target="_blank"
              className={`col-auto ${tooltipOrigin ? " ms-2" : ""}`}
              rel="noopener noreferrer"
              href={CompanyService.getWhatsappUrl(displayValue)}
            >
              <img
                src={whatsappIco}
                width="20"
                height="20"
                alt="whatsapp icon"
              />
            </a>
          )}
        </div>
      );
  }
  return highlight ? (
    <TooltipComponent title={displayValue} placement="bottom">
      {displayValue}
    </TooltipComponent>
  ) : (
    <>{displayValue}</>
  );
};

const ClientFieldDisplayByContent = ({
  title,
  value,
  link,
  isMail,
  tooltip,
  copyText,
  personId
}: {
  title: string;
  value: string | JSX.Element;
  link?: string;
  isMail?: boolean;
  tooltip?: string;
  copyText?: (text: string) => void;
  personId?: number;
}) => {
  const [urlSite, setUrlSite] = useState("");
  const [loading, setLoading] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const canRenew = CompanyService.isBackOfficeUser();

  const renewToken = async () => {
    if (renewing) {
      return;
    }
    setRenewing(true);
    const result = await ClientService.refreshToken(personId!);
    if (result instanceof Error) {
      setRenewing(false);
      ToastService.showToast(
        TranslationService.translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
      return;
    }
    window.location.reload();
  };

  const confirmRenewToken = () => {
    if (renewing) {
      return;
    }
    ModalService.showDefaultModal({
      title: TranslationService.translate.RenewFollowUpMail,
      message: () => (
        <span style={{ whiteSpace: "pre-line" }}>
          {TranslationService.translate.RenewFollowUpMailConfirm}
        </span>
      ),
      acceptButtonLabel: TranslationService.translate.Accept,
      onAcceptClick: renewToken
    });
  };

  const copyTextUrlSite = async (personId: number) => {
    if (urlSite === "") {
      try {
        setLoading(true);
        const result = await ClientService.getShortUrlCustomerSite(personId);
        if (result instanceof Error) {
          setLoading(false);
          ToastService.showToast(
            TranslationService.translate.ErrorProcessingRequest,
            undefined,
            "danger"
          );
          return;
        }

        setUrlSite(result.customerSite);
        copyText!(result.customerSite);
        setLoading(false);
      } catch (error) {
        ToastService.showToast(
          TranslationService.translate.ErrorProcessingRequest,
          undefined,
          "danger"
        );
      }
    } else {
      copyText!(urlSite);
    }
  };

  return (
    <li className="row aboutCompanyItem">
      <h5 className="col-12">{title} </h5>
      <div className="col-12 pe-0" style={{ verticalAlign: "middle" }}>
        {link && (
          <a
            className="ellipsis-oneline d-inline"
            href={(isMail ? "mailto:" : "") + link}
            rel="noreferrer"
            target="_blank"
          >
            {value}
            <i className="far fa-external-link ms-2"></i>
          </a>
        )}
        {link && copyText && isMail && (
          <TooltipComponent
            title={TranslationService.translate.CopyToClipboard}
            placement="right"
          >
            <i
              className="fal fa-copy is-link px-2"
              onClick={() => {
                copyText(link);
              }}
            />
          </TooltipComponent>
        )}
        {link && isMail && canRenew && personId !== undefined && (
          <TooltipComponent
            title={TranslationService.translate.RenewFollowUpMail}
            placement="right"
          >
            <i
              className={`fal ${
                renewing ? "fa-spinner-third fa-spin" : "fa-sync"
              } is-link px-2`}
              onClick={confirmRenewToken}
            />
          </TooltipComponent>
        )}
        {link && copyText && !isMail && (
          <TooltipComponent
            title={TranslationService.translate.CopyToClipboard}
            placement="right"
          >
            <i
              className="fal fa-copy is-link px-2"
              onClick={() => {
                copyTextUrlSite(personId!);
              }}
            />
          </TooltipComponent>
        )}
        {!link && <label>{value}</label>}
        {loading && !isMail ? (
          <i className="fa-fw fa-solid fa-spinner-third fa-spin third ms-1"></i>
        ) : (
          <></>
        )}
      </div>
    </li>
  );
};

const copyText = (text: string) => {
  navigator.clipboard.writeText(text);
  ToastService.showToast(
    TranslationService.translate.CopiedToClipboard,
    undefined,
    "success"
  );
};

const ClientFieldDisplay = ({
  field,
  client,
  showAnyway = false,
  onClientSave
}: {
  field: EntityField;
  client: Client;
  showAnyway?: boolean;
  onClientSave?: () => void;
}) => {
  const title = field.title;
  const [value, setValue] = useState(() => getValue(field, client) || "");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputValue = useRef<string>(value ?? "");

  useEffect(() => {
    setValue(() => getValue(field, client));

    return () => {
      setValue(undefined);
    };
  }, [client, field]);

  if (!value && !showAnyway) {
    return <></>;
  }
  const onSave = async () => {
    setSaving(true);
    if (getClientFieldIsDefault(field.id)) {
      const key = getClientDefaultFieldProperty(field.id);
      Cast<Record<string, string>>(client)[key] = inputValue.current;
    } else {
      const additional = client.additionals.find(
        (x) => x.id === field.id || x["Id" as keyof typeof x] === field.id
      );
      if (additional) {
        additional.value = inputValue.current;
      } else {
        const additional = { id: field.id, value: inputValue.current };
        client.additionals.push(additional);
      }
    }

    await handleErrorWithToast(
      ClientService.setField(
        client.PersonId.toString(),
        field.id,
        inputValue.current
      ),
      () => setSaving(false)
    );

    setValue(inputValue.current);
    setSaving(false);
    setEditing(false);
    onClientSave && onClientSave();
  };
  const dropdownItems =
    field.type !== DataType.List
      ? []
      : CompanyService.getAdditionalDefinitions()
          .find((x) => x.AdditionalDefinitionID === field.id)!
          .AdditionalDefinitionItems.map((x) => ({
            text: x.Value,
            value: x.AdditionalDefinitionItemID
          }));
  const dropdownItemValue =
    dropdownItems.find((x) => x.value?.toString() === value?.toString())
      ?.value || [];

  return (
    <li className="row aboutCompanyItem">
      <h5 className="col-12">{title}</h5>
      <div className="col-12 pe-0 show-child-on-hover">
        {!editing && (
          <div className="d-flex align-items-center justify-content-between">
            <p className="ellipsis-oneline d-flex break-word">
              {getClientFieldDisplayValueHtml(field, client, false, copyText)}
            </p>
            {field.ManualEdit &&
              CompanyService.canDo("editperson") &&
              !CompanyService.getSetting("hideeditperson") && (
                <i
                  className="fa-regular fa-pen px-2 show-when-hovering-parent pointer"
                  onClick={() => setEditing(true)}
                />
              )}
          </div>
        )}
        {editing && (
          <div className="row">
            <div className="col p-0">
              {field.type === DataType.Date ? (
                <DatePickerEditor
                  onChange={(val) =>
                    (inputValue.current = val
                      ? formatIntizaOldDate(new Date(val))
                      : "")
                  }
                  defaultValue={value?.toString()}
                />
              ) : field.type === DataType.List ? (
                <Dropdown
                  items={dropdownItems}
                  defaultValue={dropdownItemValue}
                  onChange={(value) => (inputValue.current = value.toString())}
                />
              ) : field.type === DataType.Number ? (
                <NumberInput
                  className="form-control"
                  allowDecimal
                  allowNegative
                  defaultValue={value?.toString()}
                  onChange={(e) => (inputValue.current = e.target.value)}
                />
              ) : (
                <input
                  className="form-control"
                  type="text"
                  maxLength={
                    field.type === DataType.Text ? field.maxLength : undefined
                  }
                  defaultValue={value?.toString()}
                  onChange={(e) => (inputValue.current = e.target.value)}
                />
              )}
            </div>

            <div className="col-auto p-0 d-flex align-items-center">
              {saving ? (
                <>
                  <i className="fa-fw fa-solid fa-check react-datepicker__day--outside-month ms-2"></i>
                  <i className="fa-fw fa-solid fa-spinner-third fa-spin third ms-1"></i>
                </>
              ) : (
                <>
                  <i
                    className="fa-fw fa-solid fa-check ms-2 text-primary pointer"
                    onClick={onSave}
                  />
                  <i
                    className="fa-fw fa-solid fa-times ms-1 text-danger pointer"
                    onClick={() => {
                      setEditing(false);
                      inputValue.current = value;
                    }}
                  />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </li>
  );
};

function getValue(field: EntityField, client: Client) {
  const displayValue = getClientFieldDisplayValue(field, client);
  if (field.type !== DataType.List) {
    return displayValue;
  }
  return CompanyService.getAdditionalDefinitions()
    .find((x) => x.AdditionalDefinitionID === field.id)!
    .AdditionalDefinitionItems.find((x) => x.Value === displayValue)
    ?.AdditionalDefinitionItemID.toString();
}

const ClientFieldDisplayRight = ({
  field,
  client
}: {
  field: EntityField;
  client: Client;
}) => {
  const title = field.title;
  const displayValue = getClientFieldDisplayValueHtml(
    field,
    client,
    true,
    copyText
  );
  if (!displayValue) {
    return <></>;
  }
  return (
    <div className="creditLimitBox">
      <h5>{title}</h5>
      <p
        style={{ display: "flex", flexDirection: "row" }}
        className="overflow-hidden link-to"
      >
        {displayValue}
      </p>
    </div>
  );
};

type ExtraValues = Exclude<
  Awaited<ReturnType<typeof ClientService.getExtraValues>>,
  Error
>;
export const ClientDetail = () => {
  const { translate, currentLanguage } = TranslationService;
  const [client, setClient] = useState<Client | null | undefined>();
  const [clientIdResult, setClientIdResult] = useState<number>();
  const [agrupadoId, setAgrupadoId] = useState<number>();
  const [loadingAgrupado, setLoadingAgrupado] = useState<boolean>(false);
  const [agrupado, setAgrupado] = useState<Client | null | undefined>();
  const [customerToken, setCustomerToken] = useState<string>();
  const groupRef = useRef<{
    setValue: (state: { value: number; label: string }) => void;
  }>();
  //const [pendingReceivables, setPendingReceivables] = useState(0);
  const [segments, setSegments] = useState<string>();
  const [extraValues, setExtraValues] = useState<ExtraValues>();

  const [groupId, setGroupId] = useState<number>();
  const [showAging, setShowAging] = useState<boolean>(false);
  const needClientCurrency = CompanyService.getSetting(
    BadlySpelled2("showtotalincustomercurrency", "showtotalintcustomercurrency")
  );
  const [displayCurrencyId, setDisplayCurrencyId] = useState<number>(
    needClientCurrency ? 0 : CompanyService.getDefaultCurrencyId()
  );
  const { setCurrentAuthentication } = useContext(AuthenticationContext);
  const requestAborter = useRequestAborter();

  useEffect(() => {
    if (needClientCurrency && !displayCurrencyId) {
      RequestService.intercept("/io/list", (result: any) => {
        // result es el objeto completo de axios, los datos están en result.data
        const currencyId = result?.data?.currencyId;
        if (currencyId && currencyId > 0) {
          setDisplayCurrencyId(currencyId);
        }
      });
    }
  }, [displayCurrencyId, needClientCurrency]);

  // Fallback: si después de cargar el cliente no se ha obtenido el currencyId y el cliente no tiene facturas,
  // usar la moneda por defecto para evitar que quede en Loading indefinidamente
  useEffect(() => {
    if (
      needClientCurrency &&
      !displayCurrencyId &&
      client &&
      client.ioCount === 0
    ) {
      // Si el cliente no tiene facturas, usar la moneda por defecto
      setDisplayCurrencyId(CompanyService.getDefaultCurrencyId());
    }
  }, [needClientCurrency, displayCurrencyId, client]);

  const { state, search } = useLocation<{
    clientIterator: ClientIterator;
    prevRequest: unknown;
  }>();
  const clientId =
    new URLSearchParams(search).get("id") ??
    new URLSearchParams(search).get("personid") ??
    "";
  const key = new URLSearchParams(search).get("key") ?? undefined;
  const phone = new URLSearchParams(search).get("phone") ?? undefined;
  const gid =
    new URLSearchParams(search).get("guid") ??
    new URLSearchParams(search).get("gid") ??
    undefined;

  const openModalDefault =
    new URLSearchParams(search).get("openModalDefault") ?? "";
  const history = useHistory();
  const [showMenu, setShowMenu] = useState(
    StorageService.getPreferences().clientDetailsCollapsed
  );

  const rightColumnFields = [
    ...CompanyService.getAdditionalDefinitionsFiltered(
      Entities.Client,
      true
    ).map((x) => new EntityField(x))
  ].sort(sortEntityFields(CompanyService.getPersonSortedFields().split(",")));
  const invoiceGroups = CompanyService.getIOGroup();

  const toggleMenu = () => {
    setShowMenu((x) => {
      StorageService.setPreference("clientDetailsCollapsed", !x);
      return !x;
    });
  };

  const collGet = (x: null | HTMLDivElement) => {
    if (x === null) {
      return;
    }
    if (StorageService.getPreferences().clientDetailsCollapsed) {
      x.classList.add("show");
    }
  };

  const loadClient = useCallback(() => {
    const loadClient = async () => {
      if (agrupadoId !== undefined) {
        loadAgrupado();
      }

      const result = await ClientService.get({
        id: clientId.length > 0 ? clientId : undefined,
        personkey: key,
        phone
      });
      if (result instanceof Error) {
        setClient(null);
        !agrupadoId && setAgrupado(null);
        return;
      }
      setClientIdResult(result.id);
      !agrupadoId &&
        setSegments(result.segments?.map((x) => x.Name).join(", "));
      setClient(result.item);
      !agrupadoId && setAgrupado(result.item);
      setCustomerToken(result?.customertoken);

      const extraResult = await ClientService.getExtraValues(result.id);
      setExtraValues(extraResult instanceof Error ? undefined : extraResult);
    };
    const loadAgrupado = async () => {
      setLoadingAgrupado(true);
      const result = await ClientService.get({ id: agrupadoId!.toString() });
      if (result instanceof Error) {
        setAgrupado(null);
        setLoadingAgrupado(false);
        return;
      }
      setSegments(result.segments?.map((x) => x.Name).join(", "));
      setAgrupado(result.item);
      setCustomerToken(result?.customertoken);
      setLoadingAgrupado(false);
    };

    loadClient();
  }, [agrupadoId, clientId, key, phone]);

  useEffect(() => {
    if (gid && gid !== CompanyService.getCompanyToken().companyid) {
      const switchCompany = async () => {
        const result = await requestAborter.next(
          AuthenticationService.switchCompany(gid!)
        );
        if (result instanceof Error) {
          return;
        }
        setCurrentAuthentication(result);
      };
      switchCompany().then(() => window.location.reload());
    } else {
      loadClient();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gid, loadClient, requestAborter]);

  const convertClientCurrency = (amount: number | undefined) =>
    displayCurrencyId
      ? CurrencyService.convert(
          amount ?? 0,
          CompanyService.getDefaultCurrencyId(),
          displayCurrencyId
        )
      : 0;

  const reloads = {
    invoice: () => undefined,
    activity: () => undefined,
    client: loadClient,
    groups: () => undefined
  };
  const reload = () => {
    reloads.client();
    reloads.activity();
    reloads.groups();
    reloads.invoice();
  };

  if (client === null || agrupado === null) {
    ModalService.showDefaultModal({
      message: translate.ClientNotFound.replace(
        "{company}",
        CompanyService.getCompanyName()!
      )
    });
    return <Redirect to={"/" + currentLanguage + "/client"} />;
  }

  const setGroupFilter = (name: string, groupId: number) => {
    groupRef.current!.setValue({ value: groupId, label: name });
  };

  const openEditClientModal = () => {
    FloatingPanelService.showPanel({
      title: translate.EditClient,
      children: (
        <ClientEdit
          client={client}
          onClientSave={() => reload()}
          onClientDeleted={() => {
            history.push(`/${currentLanguage}/client`);
          }}
        />
      ),
      width: 600,
      height: 700,
      position: "bottom-right"
    });
  };

  return (
    <>
      {clientIdResult ? (
        <ClientDetailProvider
          reloadExtraData={() => {
            loadClient();
            reloads.activity();
          }}
        >
          <TabPosition module="client">
            <section className="debts" key={clientId}>
              <div>
                {client === undefined || agrupado === undefined ? (
                  <Loading />
                ) : (
                  <div className="row flex-nowrap g-0 app-height">
                    <Helmet>
                      <title>Intiza - {agrupado.Name}</title>
                    </Helmet>
                    <div className="col-auto p-0 debtsMenu h-100">
                      <div
                        className="collapse collapse-horizontal h-100"
                        id="collapseMenu"
                        ref={collGet}
                      >
                        <div className="d-flex flex-column h-100">
                          <div className="resumeCompany">
                            {client?.GroupedPerson &&
                              client.GroupedPerson.length > 0 && (
                                <div
                                  className="viewData row"
                                  style={{ alignItems: "center" }}
                                >
                                  <div className="col-5">
                                    <label style={{ fontSize: "13px" }}>
                                      {
                                        <>
                                          {translate.ViewDataFrom}:
                                          {loadingAgrupado && (
                                            <i className="fas fa-spinner-third fa-spin third ms-2" />
                                          )}
                                        </>
                                      }
                                    </label>
                                  </div>
                                  <div className="col">
                                    <Dropdown
                                      secondaryVariant
                                      items={client.GroupedPerson.map((x) => ({
                                        text: x.Name,
                                        value: x.PersonId
                                      }))}
                                      onChange={setAgrupadoId}
                                      optionLabel={translate.MainCompany}
                                      selectClasses="col"
                                    />
                                  </div>
                                </div>
                              )}
                            <ClientDetailLeftMenuButtons
                              client={client}
                              clientEmail={client?.Email}
                              reload={reload}
                              toggleMenu={() => toggleMenu()}
                              direction={"column"}
                              groupId={groupId}
                            />
                            <ClientDetailLeftMenuButtons
                              client={client}
                              clientEmail={client?.Email}
                              reload={reload}
                              toggleMenu={() => toggleMenu()}
                              direction="row"
                              groupId={groupId}
                            />
                          </div>
                          <AboutCompany
                            extraValues={extraValues}
                            agrupado={agrupado}
                            showMenu={!!showMenu}
                            openEditClientModal={openEditClientModal}
                            customerToken={customerToken}
                            onClientSave={reload}
                            segments={segments}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col debtsDetail h-100 overflow-auto">
                      <div className="ps-3 mb-0 pt-4 position-relative z-index-10">
                        <BreadcrumbHistory
                          defaultPrevious={{
                            link: () => {
                              history.push(
                                `/${TranslationService.currentLanguage}/client`,
                                { prevRequest: state.prevRequest }
                              );
                            },
                            name:
                              state?.clientIterator?.items?.length > 0
                                ? TranslationService.translate.Clients +
                                  ` (${TranslationService.translate.Filtered})`
                                : translate.Clients
                          }}
                          current={{
                            name: client.Name,
                            link: `client/details?id=${client.PersonId}`
                          }}
                        >
                          <span className="breadcrumb-controls ms-auto d-flex align-items-center flex-row mb-0">
                            {state?.clientIterator?.hasPrev && (
                              <>
                                {state.clientIterator.hasPrev() && (
                                  <button
                                    className="btn btn-link"
                                    onClick={() => {
                                      setClient(undefined);
                                      state.clientIterator.goPrev(history);
                                    }}
                                  >
                                    {TranslationService.translate.Previous}
                                  </button>
                                )}
                                {state.clientIterator.hasPrev() &&
                                  state.clientIterator.hasNext() && <>|</>}
                                {state.clientIterator.hasNext() && (
                                  <button
                                    className="btn btn-link"
                                    onClick={async () => {
                                      await state.clientIterator.goNext(
                                        history
                                      );
                                      setClient(undefined);
                                    }}
                                  >
                                    {TranslationService.translate.Next}
                                  </button>
                                )}
                              </>
                            )}
                            <span className="ms-2 mb-1" style={{ width: 200 }}>
                              <AutocompleteClient
                                placeholder={
                                  TranslationService.translate.SearchCustomer
                                }
                                onChange={(x) => {
                                  if (x) {
                                    setClient(undefined);
                                    history.push(
                                      `/${currentLanguage}/client/details?id=${x}`
                                    );
                                  }
                                }}
                              />
                            </span>
                          </span>
                        </BreadcrumbHistory>
                      </div>
                      <FixToTop
                        isFixed
                        backgroundClass=""
                        id="clientTitleHeight"
                      >
                        <div className="ps-3 sticky-clientname d-flex justify-content-between align-items-start">
                          {CompanyService.canDo("editperson") &&
                          !CompanyService.getSetting("hideeditperson") ? (
                            <div className={"nameCompany canEdit"}>
                              <h2 onClick={openEditClientModal}>
                                {agrupado.Name}
                                <TooltipComponent
                                  title={
                                    showAging
                                      ? translate.HideAging
                                      : translate.ShowAging
                                  }
                                  placement="right"
                                >
                                  <img
                                    src={
                                      showAging
                                        ? iconAgingActive
                                        : iconAgingInactive
                                    }
                                    alt="toggle aging"
                                    style={{
                                      cursor: "pointer",
                                      width: "20px",
                                      height: "20px",
                                      marginLeft: "8px"
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowAging(!showAging);
                                    }}
                                  />
                                </TooltipComponent>
                                <i className="far fa-pen"></i>
                              </h2>
                            </div>
                          ) : (
                            <div className={"nameCompany mb-3"}>
                              <h2>
                                {agrupado.Name}
                                <TooltipComponent
                                  title={
                                    showAging
                                      ? translate.HideAging
                                      : translate.ShowAging
                                  }
                                  placement="right"
                                >
                                  <img
                                    src={
                                      showAging
                                        ? iconAgingActive
                                        : iconAgingInactive
                                    }
                                    alt="toggle aging"
                                    style={{
                                      cursor: "pointer",
                                      width: "20px",
                                      height: "20px",
                                      marginLeft: "8px"
                                    }}
                                    onClick={() => setShowAging(!showAging)}
                                  />
                                </TooltipComponent>
                              </h2>
                            </div>
                          )}
                          <ScrollNav />
                        </div>
                      </FixToTop>
                      <div className="tab-content p-0" id="pills-tabContent">
                        <div
                          className="tab-pane fade show active"
                          id="pills-collections"
                          role="tabpanel"
                          aria-labelledby="pills-collections-tab"
                        >
                          {
                            <div className="mt-3">
                              {displayCurrencyId > 0 && extraValues ? (
                                <DashboardClientSales
                                  averagePeriodPerson={formatInteger(
                                    extraValues.averagePeriodPerson
                                  )}
                                  averagePeriod={formatInteger(
                                    extraValues.averagePeriod
                                  )}
                                  averageAgingPerson={formatInteger(
                                    extraValues.averageAgingPerson
                                  )}
                                  averageAging={formatInteger(
                                    extraValues.averageAging
                                  )}
                                  totalAmount={convertClientCurrency(
                                    client.amount
                                  )}
                                  currencyId={displayCurrencyId}
                                />
                              ) : (
                                <Loading height={90} />
                              )}
                            </div>
                          }
                          {
                            <>
                              {showAging && (
                                <DashboardClientPanel
                                  clientId={parseInt(clientId)}
                                />
                              )}
                              {(client.CreditLimit ?? 0) > 0 && (
                                <div className="card mt-3 p-0">
                                  <ClientDashboardExtraValues
                                    {...{
                                      client,
                                      extraValues,
                                      displayCurrencyId,
                                      convertClientCurrency
                                    }}
                                  />
                                </div>
                              )}
                              {rightColumnFields && (
                                <div className="row creditLimit">
                                  <div className="creditLimitGrid">
                                    {rightColumnFields.map((field) => (
                                      <ClientFieldDisplayRight
                                        key={field.id}
                                        field={field}
                                        client={agrupado}
                                      />
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>
                          }
                          <InvoiceGroupProvider personid={client.PersonId}>
                            {
                              <>
                                {invoiceGroups?.length > 0 && (
                                  <div className="mt-3">
                                    <InvoiceGroups groups={invoiceGroups} />
                                  </div>
                                )}
                              </>
                            }
                            {
                              <>
                                {
                                  <>
                                    {CompanyService.getSetting(
                                      "showgrouptable"
                                    ) && (
                                      <div className="mt-3">
                                        <InvoiceGroupTableProvider
                                          personid={client.PersonId}
                                        >
                                          <GroupTable
                                            personId={parseInt(clientId)}
                                            setReloads={reloads}
                                            setGroupFilter={setGroupFilter}
                                            actions={
                                              <div className="col-3 group-filter d-flex align-items-center">
                                                <b className="me-3">
                                                  Filtrar por{" "}
                                                  {CompanyService.getGroupName()}
                                                  :{" "}
                                                </b>
                                                <div className="flex-grow-1">
                                                  <AutocompleteGroup
                                                    clientId={clientId.toString()}
                                                    onChange={(x) =>
                                                      setGroupId(
                                                        x && parseInt(x.value)
                                                      )
                                                    }
                                                    ref={groupRef}
                                                  />
                                                </div>
                                              </div>
                                            }
                                          />
                                        </InvoiceGroupTableProvider>
                                      </div>
                                    )}
                                  </>
                                }

                                <div className="card mt-3 p-0">
                                  <section className="clientsTabs">
                                    <div className="tab-content p-0">
                                      {
                                        <div
                                          className="tab-pane fade show active"
                                          id="invoices-tab-pane"
                                          role="tabpanel"
                                          aria-labelledby="home-tab"
                                          tabIndex={0}
                                        >
                                          <InvoiceList
                                            client={client}
                                            groupId={groupId}
                                            setReloads={reloads}
                                            reload={reload}
                                          />
                                        </div>
                                      }
                                    </div>
                                  </section>
                                </div>
                                <div className="mt-0 p-0">
                                  <section className="clientsTabs">
                                    {/*COMENTADO HASTA TENER MAS DE UNA OPCION
                                                            https://intizadev.atlassian.net/browse/GDC-447

                                                            <ul className="nav nav-pills" id="activity-tab" role="tablist">
                                                            <li className="nav-item" role="presentation">
                                                                <button className="nav-link active" data-bs-toggle="pill" data-bs-target="#activity-tab-pane" type="button" role="tab"><p><i className="far fa-history me-2"></i> {translate.ActivityHistory}</p><span className="rounded"></span></button>
                                                            </li>
                                                            </ul>*/}
                                    <div className="tab-content">
                                      <div
                                        className="tab-pane fade show active"
                                        id="activity-tab-pane"
                                        role="tabpanel"
                                        aria-labelledby="profile-tab"
                                        tabIndex={0}
                                      >
                                        <ActivityHistory
                                          personid={clientIdResult.toString()}
                                          person={client}
                                          emailAddress={client.Email}
                                          groupid={groupId}
                                          setReloads={reloads}
                                          openModalDefault={openModalDefault}
                                          reload={reload}
                                        />
                                      </div>
                                    </div>
                                  </section>
                                </div>
                              </>
                            }
                          </InvoiceGroupProvider>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </TabPosition>
          <FirstLoadClient
            load={client === undefined || agrupado === undefined}
          />
        </ClientDetailProvider>
      ) : null}
    </>
  );
};

const FirstLoadClient = ({ load }: { load: boolean }) => {
  const clientDetailContextValues = useContext(ClientDetailContext);

  useEffect(() => {
    if (load) {
      clientDetailContextValues.setInvoiceIds([]);
      clientDetailContextValues.setSelectedInvoiceData(new Map());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  return <></>;
};

const getNavElement = [
  () =>
    document.querySelector("section.debts .debtsDetail")
      ?.firstChild as HTMLDivElement,
  () => document.getElementById("invoice-header-marker") as HTMLDivElement,
  () =>
    document.getElementById("pending-activity-header-marker") as HTMLDivElement,
  () => document.getElementById("activity-header-marker") as HTMLDivElement
];

const ScrollNav = () => {
  const [activeNav, setActiveNav] = useState(0);
  const scrollingRef = useRef(false);
  const goToNav = (index: number) => {
    const element = getNavElement[index]();
    const parent = getNavElement[0]().parentElement! as HTMLFormElement;
    const clientTitleHeight =
      document.getElementById("clientTitleHeight")?.getBoundingClientRect()
        .height || 0;
    const fixTopInvoices =
      document.getElementById("fixTopInvoices")?.getBoundingClientRect()
        .height || 0;
    const totalHeight = clientTitleHeight + fixTopInvoices + 35;

    const top =
      index === 0
        ? 0
        : index === 1
          ? parent.scrollTop + element.getBoundingClientRect().top - totalHeight
          : parent.scrollTop +
            element.getBoundingClientRect().top -
            totalHeight;
    if (parent.scrollTop === top) {
      return;
    }
    scrollingRef.current = true;
    parent.scrollTo({
      behavior: "smooth",
      top
    });
    setActiveNav(index);
    parent.onscrollend = () => {
      scrollingRef.current = false;
      parent.onscrollend = null;
    };
  };
  useLayoutEffect(() => {
    const interval = setInterval(() => {
      const div = getNavElement[0]();
      const invoice = getNavElement[1]();
      const task = getNavElement[2]();
      const activity = getNavElement[3]();
      if (!div || !invoice || !task || !activity) {
        return;
      }
      if (scrollingRef.current) {
        return;
      }

      if (activity.getBoundingClientRect().top < 200) {
        setActiveNav(3);
      } else if (task.getBoundingClientRect().top < 200) {
        setActiveNav(2);
      } else if (invoice.getBoundingClientRect().top < 200) {
        setActiveNav(1);
      } else {
        setActiveNav(0);
      }
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const iconTab = (iconTabValue: string) => {
    switch (iconTabValue) {
      case TranslationService.translate.Dashboard:
        return [iconHouse, iconInactiveHouse];
      case TranslationService.translate.Invoices:
        return [iconInvoice, iconInactiveInvoice];
      case TranslationService.translate.Tasks:
        return [iconTasks, iconInactiveTasks];
      case TranslationService.translate.History:
        return [iconHistory, iconInactiveHistory];
      default:
        return [iconHouse, iconInactiveHouse];
    }
  };

  const navTabs: OptionTab[] = [
    TranslationService.translate.Dashboard,
    TranslationService.translate.Invoices,
    TranslationService.translate.Tasks,
    TranslationService.translate.History
  ].map((x, index) => ({
    index,
    title: x,
    activeTab: index,
    setActiveTab: goToNav,
    iconActive: () => iconTab(x)[0],
    iconInactive: () => iconTab(x)[1]
  }));
  return (
    <ScrollNavChips
      tabs={navTabs}
      activeTab={activeNav}
      setActiveTab={goToNav}
    />
  );
};

const GroupTable = ({
  personId,
  actions,
  setGroupFilter,
  setReloads
}: {
  personId: number;
  actions?: JSX.Element;
  setGroupFilter: (groupName: string, groupId: number) => void;
  setReloads?: { groups: () => void };
}) => {
  const { response } = useContext(
    TableContext
  ) as InvoiceGroupTableContextValues;

  const headers = [
    new TableHeader("group", CompanyService.getGroupName(), false, false),
    new TableHeader("io", TranslationService.translate.Invoices, true, false),
    new TableHeader("amount", TranslationService.translate.Amount, true, false),
    new TableHeader("amountDue", TranslationService.translate.Due, true, false),
    new TableHeader(
      "amountNotDue",
      TranslationService.translate.NotDue,
      true,
      false
    ),
    new TableHeader("due", TranslationService.translate.DueNoDue, false, false)
  ];

  const max =
    response?.list
      .map((x) => x.amount)
      .reduce((prev, curr) => (prev > curr ? prev : curr), 1) || 1;

  return (
    <>
      <TableContext.Consumer>
        {({ reload }) => {
          if (setReloads) {
            setReloads.groups = reload;
          }
          return <></>;
        }}
      </TableContext.Consumer>
      {actions && <div className="mt-2">{actions}</div>}
      <div className="mt-3">
        <Table
          headers={headers}
          item={({ data }: { data: GroupListResponse.Item }) => {
            return (
              <tr
                onClick={() => setGroupFilter(data.Name, data.GroupID)}
                className="pointer"
              >
                <td colSpan={2}>{data.Name}</td>
                <td className={"text-end"}>{data.count}</td>
                <td className={"text-end "}>
                  {formatCurrency(
                    data.amount,
                    CompanyService.getDefaultCurrencyId()
                  )}
                </td>
                <td className={"text-end text-danger"}>
                  {formatCurrency(
                    data.overdueAmount,
                    CompanyService.getDefaultCurrencyId()
                  )}
                </td>
                <td className={"text-end "}>
                  {formatCurrency(
                    data.amount - data.overdueAmount,
                    CompanyService.getDefaultCurrencyId()
                  )}
                </td>
                <td className="bar-progress">
                  <div className="bar">
                    <div
                      className="expired"
                      style={{
                        width: ((data.overdueAmount ?? 0) * 100) / max + "%"
                      }}
                    ></div>
                    <div
                      className="not-expired"
                      style={{
                        width:
                          ((data.amount - data.overdueAmount || 0) * 100) /
                            max +
                          "%"
                      }}
                    ></div>
                  </div>
                </td>
              </tr>
            );
          }}
        ></Table>
      </div>
    </>
  );
};

const AboutCompany = ({
  extraValues,
  agrupado,
  showMenu,
  openEditClientModal,
  customerToken,
  onClientSave,
  segments
}: {
  extraValues: ExtraValues | undefined;
  agrupado: Client;
  showMenu: boolean;
  openEditClientModal?: () => void;
  customerToken?: string;
  onClientSave?: () => void;
  segments?: string;
}) => {
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const { currentLanguage } = TranslationService;
  const leftColumnFields = useMemo(
    () =>
      [
        ...CompanyService.getAdditionalDefinitionsFiltered(
          Entities.Client,
          false,
          undefined,
          true
        ).map((x) => new EntityField(x)),
        ...CompanyService.getConfigItemsFiltered(Entities.Client, true, [
          ClientFields.Name,
          ClientFields.Contact,
          ClientFields.Email,
          ClientFields.Phone,
          ClientFields.Address,
          ClientFields.Comments
        ]).map((x) => new EntityField(x))
      ]
        .filter((x) => (x.title ?? "").toLocaleLowerCase().includes(search))
        .sort(
          sortEntityFields(CompanyService.getPersonSortedFields().split(","))
        ),
    [search]
  );

  const portalLink =
    `${window.location.origin}/${currentLanguage}/portalclient?t=` +
    customerToken;

  return (
    <>
      {showMenu && (
        <div className="searcherFilterHeader m-3">
          <SearchInput
            onSearch={(val) => setSearch(val.toLocaleLowerCase() ?? "")}
          />
        </div>
      )}
      <div className="aboutCompany">
        <ul>
          {leftColumnFields.map((field) => (
            <ClientFieldDisplay
              key={field.id}
              field={field}
              client={agrupado}
              showAnyway={showAll}
              onClientSave={onClientSave}
            />
          ))}
          {agrupado.followUpMail &&
            TranslationService.translate.FollowUpMail.toLocaleLowerCase().includes(
              search
            ) && (
              <ClientFieldDisplayByContent
                title={TranslationService.translate.FollowUpMail}
                value={agrupado.followUpMail}
                tooltip={TranslationService.translate.FollowUpMailTip}
                link={agrupado.followUpMail}
                isMail={true}
                personId={agrupado.PersonId}
                copyText={copyText}
              ></ClientFieldDisplayByContent>
            )}

          {segments ? (
            <>
              <li className="row aboutCompanyItem">
                <h5 className="col-12">
                  {TranslationService.translate.Segments}{" "}
                </h5>
              </li>
              <TooltipComponent title={segments}>
                <div className="col-12 pe-0">
                  <label
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: "1.2em",
                      maxHeight: "3.6em",
                      color: "black",
                      fontWeight: 600,
                      fontSize: 13
                    }}
                  >
                    {segments}
                  </label>
                </div>
              </TooltipComponent>
            </>
          ) : null}
          {CompanyService.getSetting("customerportalenabled") &&
            TranslationService.translate.CustomerSite.toLocaleLowerCase().includes(
              search
            ) && (
              <ClientFieldDisplayByContent
                title={TranslationService.translate.CustomerSite}
                value={TranslationService.translate.Open}
                link={portalLink}
                personId={agrupado.PersonId}
                copyText={copyText}
              ></ClientFieldDisplayByContent>
            )}
        </ul>
      </div>
      {showMenu && (
        <div className="d-flex align-items-center justify-content-between m-3">
          {CompanyService.canDo("editperson") &&
            !CompanyService.getSetting("hideeditperson") && (
              <button
                type="button"
                className="btn btn-secondary py-1 "
                onClick={openEditClientModal}
              >
                {TranslationService.translate.EditClient}
              </button>
            )}
          <TooltipComponent title={TranslationService.translate.ShowAll}>
            <ReactSwitch
              uncheckedIcon={false}
              checkedIcon={false}
              checked={showAll}
              onChange={() => setShowAll(!showAll)}
              height={24}
              width={50}
            />
          </TooltipComponent>
        </div>
      )}
    </>
  );
};

export default ClientDetail;
